import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Ban, ChevronRight, RotateCcw, X } from 'lucide-react';
import type {
  MoveAttachmentsExtensionCount,
  MoveAttachmentsJob,
  MoveAttachmentsPhase,
  MoveAttachmentsPlan,
  MoveAttachmentsStatus,
} from '@atlas/shared';
import {
  MOVE_ATTACHMENTS_IMAGE_EXT,
  MOVE_ATTACHMENTS_KEEP_EXT,
  MOVE_ATTACHMENTS_NO_EXT_LABEL,
  moveAttachmentsSupportsRecopy,
  partitionMoveAttachmentsExtensions,
} from '@atlas/shared';
import { useAuth } from '../auth/AuthProvider.js';
import {
  cancelMoveAttachments,
  changeMoveAttachmentsScope,
  confirmMoveAttachments,
  deleteMoveAttachments,
  purgeMoveAttachmentsDestination,
  rerunMoveAttachments,
} from '../lib/callables.js';
import { formatBytes } from '../lib/redshiftExports.js';
import { moveAttachmentsDestinationUris } from '../lib/moveAttachments.js';
import { SourceBadge } from './SourceBadge.js';

const PHASE_LABELS: Record<MoveAttachmentsPhase, string> = {
  extracting: 'Extracting the archives',
  discovering: 'Checking the export',
  planning: 'Reading the inventory',
  copying: 'Copying',
  verifying: 'Checking the destination',
};

const STATUS_LABELS: Record<MoveAttachmentsStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  awaiting_confirm: 'Ready to copy',
  complete: 'Complete',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

type CopyKind = 'documents' | 'images' | 'both';

function copyKindOf(job: Pick<MoveAttachmentsJob, 'imagesOnly' | 'includeImages'>): CopyKind {
  if (job.imagesOnly === true) return 'images';
  if (job.includeImages === true) return 'both';
  return 'documents';
}

/** Types that belong to all_images/ — locked off on documents-only runs. */
const IMAGE_LOCK_EXT = new Set<string>([
  ...MOVE_ATTACHMENTS_IMAGE_EXT.map((ext) => ext.toLowerCase()),
  '.gif',
  '.webp',
  '.bmp',
  '.tif',
  '.tiff',
]);

function isImageExtension(ext: string): boolean {
  return IMAGE_LOCK_EXT.has(ext.trim().toLowerCase());
}

/**
 * Documents-only: image types stay locked off. Images-only: document types stay
 * locked off. Both: every inventory row can be toggled.
 */
function isExtensionSelectable(ext: string, kind: CopyKind): boolean {
  const image = isImageExtension(ext);
  if (kind === 'images') return image;
  if (kind === 'documents') return !image;
  return true;
}

const COPY_KIND_LABELS: Record<CopyKind, string> = {
  documents: 'Documents only',
  images: 'Images only',
  both: 'Documents and images',
};

/** One row per extension. Copy wins when any pass keeps a type another pass skips. */
function extensionInventory(
  plan: MoveAttachmentsPlan | null,
  imagePlan: MoveAttachmentsPlan | null,
  certPlan: MoveAttachmentsPlan | null,
): MoveAttachmentsExtensionCount[] {
  const rows = new Map<string, MoveAttachmentsExtensionCount>();
  for (const row of plan?.byExtension ?? []) rows.set(row.ext, { ...row });
  for (const row of [...(imagePlan?.byExtension ?? []), ...(certPlan?.byExtension ?? [])]) {
    const current = rows.get(row.ext);
    if (!current) rows.set(row.ext, { ...row });
    else if (row.action === 'copy') current.action = 'copy';
  }
  return [...rows.values()];
}

function defaultSelectedExts(
  rows: MoveAttachmentsExtensionCount[],
  kind: CopyKind,
): Set<string> {
  const imageDefault = new Set<string>(
    MOVE_ATTACHMENTS_IMAGE_EXT.map((ext) => ext.toLowerCase()),
  );
  return new Set(
    rows
      .filter((row) => {
        if (!isExtensionSelectable(row.ext, kind)) return false;
        if (row.action === 'copy') return true;
        // A documents-only dry run marks image rows 'skip'; when images are now
        // in scope, still default the standard image types on.
        return isImageExtension(row.ext) && imageDefault.has(row.ext.trim().toLowerCase());
      })
      .map((row) => row.ext),
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="w-28 shrink-0 text-[#aeaaa4]">{label}</span>
      <span className="text-[#37352f] font-mono break-all">{value}</span>
    </div>
  );
}

function statusText(job: MoveAttachmentsJob): string {
  if (job.status === 'running' && job.phase) return PHASE_LABELS[job.phase];
  return STATUS_LABELS[job.status];
}

export function MoveAttachmentsDetailModal({
  job,
  onClose,
}: {
  job: MoveAttachmentsJob & { id: string };
  onClose: () => void;
}) {
  const { user, userRecord } = useAuth();
  const isAdmin = Boolean(userRecord?.isAdmin);
  const [busy, setBusy] = useState<
    'confirm' | 'cancel' | 'delete' | 'purge' | 'rerun' | 'recopy' | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [purgeOpen, setPurgeOpen] = useState(false);

  // Bullhorn copies documents into both people/ and companies/ — never images —
  // so its inventory locks image types off, like a documents-only run.
  const isBullhorn = job.sourceSystem === 'bullhorn';
  // JobAdder/Thrive keep their folder structure and skip existing keys, so the
  // copy scope can be chosen on the "Ready to copy" screen (not only after a
  // finished run). Other sources keep the scope chosen at dry-run creation.
  const scopeEditable =
    job.status === 'awaiting_confirm' &&
    !isBullhorn &&
    moveAttachmentsSupportsRecopy(job.sourceSystem);
  const [scopeChoice, setScopeChoice] = useState<CopyKind>(() => copyKindOf(job));
  const copyKind: CopyKind = isBullhorn
    ? 'documents'
    : scopeEditable
      ? scopeChoice
      : copyKindOf(job);
  const [recopyScope, setRecopyScope] = useState<CopyKind>(() => copyKindOf(job));
  const imagesOnly = copyKind === 'images';
  const includeImages = copyKind !== 'documents';
  const companiesLabel = isBullhorn ? 'Companies' : 'Images';
  const extensions = useMemo(
    () => extensionInventory(job.plan, job.imagePlan, job.certPlan ?? null),
    [job.plan, job.imagePlan, job.certPlan],
  );
  const selecting = job.status === 'awaiting_confirm';
  const [selectedExt, setSelectedExt] = useState<Set<string>>(() =>
    defaultSelectedExts(extensions, copyKind),
  );
  const inventoryKey = `${job.id}:${copyKind}:${extensions.map((row) => `${row.ext}:${row.action}`).join(',')}`;

  useEffect(() => {
    if (!selecting || extensions.length === 0) return;
    setSelectedExt(defaultSelectedExts(extensions, copyKind));
    // Only re-seed when the dry-run inventory identity changes, not on each toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inventoryKey captures extension rows + mode
  }, [inventoryKey, selecting]);

  const live = job.status === 'queued' || job.status === 'running' || job.status === 'awaiting_confirm';
  const selectedRows = selecting
    ? extensions.filter(
        (row) => selectedExt.has(row.ext) && isExtensionSelectable(row.ext, copyKind),
      )
    : extensions.filter((row) => row.action === 'copy');
  const skippedRows = selecting
    ? extensions.filter(
        (row) => !selectedExt.has(row.ext) || !isExtensionSelectable(row.ext, copyKind),
      )
    : extensions.filter((row) => row.action === 'skip');
  const selectedCount = selectedRows.reduce((sum, row) => sum + row.count, 0);
  const selectedBytes = selectedRows.reduce((sum, row) => sum + row.bytes, 0);
  const skippedCount = skippedRows.reduce((sum, row) => sum + row.count, 0);
  const skippedBytes = skippedRows.reduce((sum, row) => sum + row.bytes, 0);
  const canCopy = selecting && selectedCount > 0;
  const canDelete = job.status !== 'queued' && job.status !== 'running';
  const plan = job.plan;
  const statsPlan = plan ?? job.imagePlan;
  const targets = moveAttachmentsDestinationUris(job);
  // A finished keep-dirs run (JobAdder, Thrive) can be re-copied with a new
  // scope: the copy skips files already at the destination, so only what is
  // missing is added — no new dry run needed.
  const canRecopy =
    moveAttachmentsSupportsRecopy(job.sourceSystem) &&
    (job.status === 'complete' || job.status === 'failed' || job.status === 'cancelled') &&
    job.expectParts != null &&
    (job.unzippedPrefixes?.length ?? 0) > 0 &&
    (isAdmin || user?.uid === job.createdBy.uid);

  function toggleExt(ext: string) {
    if (!isExtensionSelectable(ext, copyKind)) return;
    setSelectedExt((prev) => {
      const next = new Set(prev);
      if (next.has(ext)) next.delete(ext);
      else next.add(ext);
      return next;
    });
  }

  async function run(action: 'confirm' | 'cancel' | 'delete' | 'purge' | 'rerun' | 'recopy') {
    setActionError(null);
    setBusy(action);
    try {
      if (action === 'confirm') {
        const allowed = [...selectedExt].filter((ext) =>
          isExtensionSelectable(ext, copyKind),
        );
        const partitioned = partitionMoveAttachmentsExtensions(allowed, {
          imagesOnly,
          includeImages,
        });
        await confirmMoveAttachments({
          jobId: job.id,
          ...(scopeEditable ? { imagesOnly, includeImages } : {}),
          keepExt: partitioned.keepExt,
          keepImageExt: partitioned.keepImageExt,
          keepNoExt: partitioned.keepNoExt,
        });
      }
      if (action === 'cancel') await cancelMoveAttachments({ jobId: job.id });
      if (action === 'rerun') await rerunMoveAttachments({ jobId: job.id });
      if (action === 'recopy') {
        await changeMoveAttachmentsScope({
          jobId: job.id,
          imagesOnly: recopyScope === 'images',
          includeImages: recopyScope !== 'documents',
        });
      }
      if (action === 'delete') {
        await deleteMoveAttachments({ jobId: job.id });
        onClose();
      }
      if (action === 'purge') {
        await purgeMoveAttachmentsDestination({ jobId: job.id });
        setPurgeOpen(false);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[680px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <h2 className="text-sm font-semibold text-[#37352f] font-mono">{job.firm}</h2>
            <p className="text-xs text-[#aeaaa4] font-mono mt-0.5">
              s3://{job.destBucket}/{job.destPrefix}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canCopy && (
              <button
                type="button"
                onClick={() => void run('confirm')}
                disabled={busy !== null}
                className="px-2.5 py-1.5 text-xs font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50"
              >
                {busy === 'confirm' ? 'Starting…' : 'Copy files'}
              </button>
            )}
            {live && (
              <button
                type="button"
                onClick={() => void run('cancel')}
                disabled={busy !== null}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#e03e3e] border border-[#f5c6c6] rounded hover:bg-[#fbe8e8] disabled:opacity-50 transition-colors"
              >
                <Ban size={13} />
                {busy === 'cancel' ? 'Stopping…' : 'Stop'}
              </button>
            )}
            {(job.status === 'failed' || job.status === 'cancelled') && (
              <button
                type="button"
                onClick={() => void run('rerun')}
                disabled={busy !== null}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f7f7f5] disabled:opacity-50 transition-colors"
              >
                <RotateCcw size={13} />
                {busy === 'rerun' ? 'Starting…' : 'Rerun'}
              </button>
            )}
            <button onClick={onClose} className="text-[#aeaaa4] hover:text-[#37352f]">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-5 pb-5 overflow-y-auto overflow-x-hidden flex flex-col gap-4 min-w-0">
          <div className="flex flex-col gap-1.5 rounded border border-[#e9e9e7] bg-[#fafafa] px-3 py-2.5">
            <Field label="Status" value={statusText(job)} />
            <Field
              label="Copy"
              value={
                isBullhorn
                  ? 'Documents → people/, companies/ and certifications/'
                  : COPY_KIND_LABELS[copyKind]
              }
            />
            <div className="flex items-center gap-2 text-xs">
              <span className="w-28 shrink-0 text-[#aeaaa4]">Source system</span>
              <SourceBadge system={job.sourceSystem} />
            </div>
            <Field label="Agency" value={job.firm} />
            <Field label="Source path" value={job.sourcePath} />
            <Field
              label="Dry run by"
              value={`${job.createdBy.displayName} · ${new Date(job.createdAt).toLocaleString('en-GB')}`}
            />
            {job.confirmedBy && job.confirmedAt && (
              <Field
                label="Copied by"
                value={`${job.confirmedBy.displayName} · ${new Date(job.confirmedAt).toLocaleString('en-GB')}`}
              />
            )}
            {job.expectParts != null && job.expectParts > 0 && (
              <Field label="Part folders" value={String(job.expectParts)} />
            )}
          </div>

          {canRecopy && (
            <div className="flex flex-col gap-2 rounded border border-[#e9e9e7] bg-[#fafafa] px-3 py-2.5">
              <h3 className="text-xs font-medium text-[#73726e]">Re-run with a different scope</h3>
              <div className="flex items-center gap-2">
                <select
                  value={recopyScope}
                  onChange={(event) => setRecopyScope(event.target.value as CopyKind)}
                  disabled={busy !== null}
                  className="text-xs border border-[#e9e9e7] rounded px-2 py-1.5 bg-white text-[#37352f] disabled:opacity-50"
                >
                  <option value="documents">{COPY_KIND_LABELS.documents}</option>
                  <option value="both">{COPY_KIND_LABELS.both}</option>
                  <option value="images">{COPY_KIND_LABELS.images}</option>
                </select>
                <button
                  type="button"
                  onClick={() => void run('recopy')}
                  disabled={busy !== null}
                  className="px-2.5 py-1.5 text-xs font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50"
                >
                  {busy === 'recopy' ? 'Starting…' : 'Run'}
                </button>
              </div>
              <p className="text-xs text-[#73726e]">
                Copies only what&apos;s missing — files already at the destination are skipped, and
                the first copy&apos;s document selection is reused.
                {recopyScope !== 'documents' && !job.imagePlan
                  ? ` Images use the default types (${MOVE_ATTACHMENTS_IMAGE_EXT.join(', ')}).`
                  : ''}
              </p>
            </div>
          )}

          {job.unzippedPrefixes && job.unzippedPrefixes.length > 0 && (
            <Collapsed
              title={`${job.sourceSystem === 'jobadder' ? 'Month folders' : isBullhorn ? 'Extracted folders' : job.sourceSystem === 'thrive' ? 'Docs folders' : 'Unzipped exports'} · ${job.unzippedPrefixes.length.toLocaleString('en-GB')}`}
            >
              <ul className="text-xs font-mono text-[#37352f] flex flex-col gap-0.5 min-w-0">
                {job.unzippedPrefixes.map((prefix) => (
                  <li key={prefix} className="break-all">
                    s3://{job.sourceBucket}/{prefix}
                  </li>
                ))}
              </ul>
            </Collapsed>
          )}

          {job.error && (
            <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
              {job.error}
            </p>
          )}
          {actionError && (
            <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
              {actionError}
            </p>
          )}

          {statsPlan && (
            <>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Stat
                  label="Will copy"
                  value={`${(selecting ? selectedCount : statsPlan.kept).toLocaleString('en-GB')} · ${formatBytes(selecting ? selectedBytes : statsPlan.keptBytes)}`}
                />
                <Stat
                  label="Skipped by type"
                  value={`${skippedCount.toLocaleString('en-GB')} · ${formatBytes(skippedBytes)}`}
                />
                <Stat label="Source objects" value={statsPlan.sourceObjects.toLocaleString('en-GB')} />
                <Stat
                  label="Skipped, same name"
                  value={`${statsPlan.skippedDuplicates.toLocaleString('en-GB')} · ${formatBytes(statsPlan.skippedDuplicateBytes)}`}
                  warn={statsPlan.skippedDuplicates > 0}
                />
              </div>

              {job.progress && job.status === 'running' && (
                <p className="text-xs text-[#73726e]">
                  {job.progress.done.toLocaleString('en-GB')} / {job.progress.total.toLocaleString('en-GB')}{' '}
                  processed · {job.progress.copied.toLocaleString('en-GB')} copied ·{' '}
                  {job.progress.skipped.toLocaleString('en-GB')} already there
                </p>
              )}

              {job.result && (
                <Collapsed title="Destination">
                  <p className="text-xs text-[#37352f]">
                    Destination holds {job.result.destObjects.toLocaleString('en-GB')} objects (
                    {formatBytes(job.result.destBytes)}).{' '}
                    {job.result.verified
                      ? 'That matches the plan.'
                      : 'That does not match the plan.'}{' '}
                    {job.result.copied.toLocaleString('en-GB')} copied,{' '}
                    {job.result.skipped.toLocaleString('en-GB')} already present.
                  </p>
                </Collapsed>
              )}

              {scopeEditable && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#73726e]">Copy</span>
                  <select
                    value={scopeChoice}
                    onChange={(event) => setScopeChoice(event.target.value as CopyKind)}
                    disabled={busy !== null}
                    className="text-xs border border-[#e9e9e7] rounded px-2 py-1.5 bg-white text-[#37352f] disabled:opacity-50"
                  >
                    <option value="documents">{COPY_KIND_LABELS.documents}</option>
                    <option value="both">{COPY_KIND_LABELS.both}</option>
                    <option value="images">{COPY_KIND_LABELS.images}</option>
                  </select>
                  <span className="text-[#aeaaa4]">— choose before copying</span>
                </div>
              )}
              {selecting && copyKind !== 'both' && (
                <p className="text-xs text-[#73726e]">
                  {isBullhorn
                    ? 'The selection applies to people/, companies/ and certifications/. Image extensions are listed for reference and cannot be copied.'
                    : copyKind === 'documents'
                      ? 'Documents only — image extensions are listed for reference and cannot be copied.'
                      : 'Images only — document extensions are listed for reference and cannot be copied.'}
                </p>
              )}
              <Inventory
                title={selecting ? 'By extension — choose what to copy' : 'By extension'}
                defaultOpen={selecting}
                rows={extensions.map((row) => {
                  const selectable = isExtensionSelectable(row.ext, copyKind);
                  return {
                    label: row.ext,
                    count: row.count,
                    bytes: row.bytes,
                    extra: selecting ? undefined : row.action,
                    checked: selecting && selectable ? selectedExt.has(row.ext) : false,
                    locked: selecting && !selectable,
                  };
                })}
                onToggle={selecting ? toggleExt : undefined}
              />
              <Inventory
                title={job.sourceSystem === 'jobadder' ? 'By month' : 'By source folder'}
                rows={statsPlan.byFolder.map((row) => ({
                  label: row.folder,
                  count: row.count,
                  bytes: row.bytes,
                }))}
              />

              {plan && plan.parts.length > 0 && (
                <Collapsed title="Part folders">
                  <ul className="text-xs font-mono text-[#37352f] flex flex-col gap-0.5">
                    {plan.parts.map((name, index) => (
                      <li key={`${name}-${index}`}>{name}</li>
                    ))}
                  </ul>
                </Collapsed>
              )}

              {plan?.duplicateLog && (
                <p className="text-xs text-[#73726e]">
                  Same-name log{' '}
                  <span className="font-mono text-[#37352f]">
                    s3://{job.destBucket}/{plan.duplicateLog}
                  </span>
                </p>
              )}
            </>
          )}

          {job.imagePlan && (
            <div className="flex flex-col gap-1">
              <h3 className="text-xs font-medium text-[#73726e]">{companiesLabel}</h3>
              <p className="text-xs text-[#37352f]">
                {job.imagePlan.kept.toLocaleString('en-GB')} unique names ·{' '}
                {formatBytes(job.imagePlan.keptBytes)}
                {job.imagePlan.skippedDuplicates > 0
                  ? ` · ${job.imagePlan.skippedDuplicates.toLocaleString('en-GB')} skipped, same name`
                  : ''}
              </p>
              {job.imagePlan.duplicateLog && (
                <p className="text-xs text-[#73726e]">
                  Same-name log{' '}
                  <span className="font-mono text-[#37352f]">
                    s3://{job.destBucket}/{job.imagePlan.duplicateLog}
                  </span>
                </p>
              )}
            </div>
          )}

          {isBullhorn && job.certPlan && (
            <div className="flex flex-col gap-1">
              <h3 className="text-xs font-medium text-[#73726e]">Certifications</h3>
              <p className="text-xs text-[#37352f]">
                {job.certPlan.kept.toLocaleString('en-GB')} unique names ·{' '}
                {formatBytes(job.certPlan.keptBytes)}
                {job.certPlan.skippedDuplicates > 0
                  ? ` · ${job.certPlan.skippedDuplicates.toLocaleString('en-GB')} skipped, same name`
                  : ''}
              </p>
              {job.certPlan.duplicateLog && (
                <p className="text-xs text-[#73726e]">
                  Same-name log{' '}
                  <span className="font-mono text-[#37352f]">
                    s3://{job.destBucket}/{job.certPlan.duplicateLog}
                  </span>
                </p>
              )}
            </div>
          )}

          {canCopy && job.sourceSystem === 'jobadder' && (
            <p className="text-xs text-[#73726e]">
              Copying uses server-side copies. Month folders are kept under{' '}
              <span className="font-mono">all_files/</span>
              {job.includeImages || job.imagesOnly ? (
                <>
                  {' '}
                  and <span className="font-mono">all_images/</span>
                </>
              ) : null}
              . Defaults are {MOVE_ATTACHMENTS_KEEP_EXT.join(', ')}
              {` and ${MOVE_ATTACHMENTS_NO_EXT_LABEL}`}. Images default to{' '}
              {MOVE_ATTACHMENTS_IMAGE_EXT.join(', ')}. A file already at the destination is
              skipped, so a stopped run can be repeated. When two files share a destination
              name, one is kept and the rest go to the same-name log. The source backup is
              left in place.
            </p>
          )}
          {canCopy && isBullhorn && (
            <p className="text-xs text-[#73726e]">
              Copying uses server-side copies. People attachments land in{' '}
              <span className="font-mono">people/</span>, company attachments in{' '}
              <span className="font-mono">companies/</span>, and certifications in{' '}
              <span className="font-mono">certifications/</span>, keeping their{' '}
              <span className="font-mono">&lt;id&gt;/&lt;YYYY.MM&gt;/</span> folders. A file already
              at the destination is skipped, so a stopped run can be repeated. After a verified
              copy the unzipped staging files are deleted; the source archives are left in place.
            </p>
          )}
          {canCopy && job.sourceSystem === 'thrive' && (
            <p className="text-xs text-[#73726e]">
              Copying uses server-side copies. Every folder inside{' '}
              <span className="font-mono">docs/</span> is kept, so files land under{' '}
              <span className="font-mono">all_files/</span> and{' '}
              <span className="font-mono">all_images/</span> with their structure intact (e.g.{' '}
              <span className="font-mono">all_images/avatars/1/…</span>). When two files share a
              destination path, one is kept and the rest go to the same-name log. A file already
              at the destination is skipped, so a stopped run can be repeated. The source export
              is left in place.
            </p>
          )}
          {canCopy && job.sourceSystem !== 'jobadder' && job.sourceSystem !== 'thrive' && !isBullhorn && (
            <p className="text-xs text-[#73726e]">
              Copying uses server-side copies. When two files share a name, one is copied and
              the other is written to the same-name log. Images, when selected, land in{' '}
              <span className="font-mono">all_images/</span>. A destination that already has
              files cannot be copied again. The source export is left in place.
            </p>
          )}
          {selecting && !canCopy && (
            <p className="text-xs text-[#e03e3e]">Select at least one extension to copy.</p>
          )}

          {isBullhorn && job.unzippedCleanupStatus === 'pending' && (
            <p className="text-xs text-[#b5820b]">
              Cleaning up extracted staging files…
              {(job.unzippedCleanupDeleted ?? 0) > 0
                ? ` ${(job.unzippedCleanupDeleted ?? 0).toLocaleString('en-GB')} removed`
                : ''}
            </p>
          )}
          {isBullhorn && job.unzippedCleanupStatus === 'complete' && (
            <p className="text-xs text-[#0f7b6c]">
              Extracted staging files deleted
              {(job.unzippedCleanupDeleted ?? 0) > 0
                ? ` (${(job.unzippedCleanupDeleted ?? 0).toLocaleString('en-GB')} objects).`
                : '.'}
            </p>
          )}
          {isBullhorn && job.unzippedCleanupStatus === 'failed' && job.unzippedCleanupError && (
            <p className="text-xs text-[#e03e3e]">Cleanup failed: {job.unzippedCleanupError}</p>
          )}

          {job.purgeStatus === 'running' && (
            <p className="text-xs text-[#b5820b]">
              {job.purgeTotal == null
                ? 'Counting files to delete…'
                : `Deleted ${(job.purgeDeleted ?? 0).toLocaleString('en-GB')} of ${job.purgeTotal.toLocaleString('en-GB')}`}
            </p>
          )}
          {job.purgeStatus === 'complete' && (
            <p className="text-xs text-[#0f7b6c]">
              {(job.purgeDeleted ?? 0) === 0
                ? 'That destination was already empty.'
                : `Deleted ${(job.purgeDeleted ?? 0).toLocaleString('en-GB')} objects from the destination.`}
            </p>
          )}
          {job.purgeStatus === 'failed' && job.purgeError && (
            <p className="text-xs text-[#e03e3e]">{job.purgeError}</p>
          )}

          {isAdmin && canDelete && (
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => {
                  setActionError(null);
                  setPurgeOpen(true);
                }}
                disabled={busy !== null || job.purgeStatus === 'running' || job.purgeStatus === 'complete'}
                className="text-xs text-[#e03e3e] hover:underline disabled:opacity-50 disabled:hover:no-underline disabled:cursor-not-allowed"
              >
                {job.purgeStatus === 'running' ? 'Deleting files…' : 'Delete files from S3'}
              </button>
              <button
                type="button"
                onClick={() => void run('delete')}
                disabled={busy !== null}
                className="text-xs text-[#aeaaa4] hover:text-[#e03e3e] disabled:opacity-50"
              >
                {busy === 'delete' ? 'Deleting…' : 'Remove from history'}
              </button>
            </div>
          )}
        </div>
      </div>

      {purgeOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
          onClick={(e) => {
            e.stopPropagation();
            if (busy !== 'purge') setPurgeOpen(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[480px] max-w-[92vw] p-5 flex flex-col gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-[#37352f]">Delete files from S3?</h3>
            <p className="text-sm text-[#37352f]">This deletes every object under:</p>
            <ul className="text-xs font-mono text-[#37352f] flex flex-col gap-1">
              {targets.map((uri) => (
                <li key={uri}>{uri}</li>
              ))}
            </ul>
            <p className="text-sm text-[#e03e3e]">
              This can be dangerous. Files linked in the production database will be lost, and
              that cannot be undone.
            </p>
            {actionError && <p className="text-xs text-[#e03e3e]">{actionError}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPurgeOpen(false)}
                disabled={busy === 'purge'}
                className="px-2.5 py-1.5 text-xs text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f7f7f5] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void run('purge')}
                disabled={busy !== null}
                className="px-2.5 py-1.5 text-xs font-medium text-white bg-[#e03e3e] rounded hover:bg-[#c92f2f] disabled:opacity-50"
              >
                {busy === 'purge' ? 'Deleting…' : 'Delete from S3'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded border border-[#e9e9e7] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-[#aeaaa4]">{label}</div>
      <div className={`text-sm font-medium ${warn ? 'text-[#e03e3e]' : 'text-[#37352f]'}`}>
        {value}
      </div>
    </div>
  );
}

function Collapsed({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details className="group rounded border border-[#e9e9e7]" open={defaultOpen || undefined}>
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs font-medium text-[#73726e] [&::-webkit-details-marker]:hidden">
        <ChevronRight
          size={13}
          className="shrink-0 text-[#aeaaa4] transition-transform group-open:rotate-90"
        />
        {title}
      </summary>
      <div className="px-3 pb-2">{children}</div>
    </details>
  );
}

function Inventory({
  title,
  rows,
  onToggle,
  defaultOpen = false,
}: {
  title: string;
  rows: Array<{
    label: string;
    count: number;
    bytes: number;
    extra?: string;
    checked?: boolean;
    /** Out of scope for this run's copy mode — no checkbox. */
    locked?: boolean;
  }>;
  onToggle?: (label: string) => void;
  defaultOpen?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <Collapsed title={title} defaultOpen={defaultOpen}>
      <table className="w-full text-xs">
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.label}
              className={`border-t border-[#f1f1ef] ${row.locked ? 'opacity-50' : ''}`}
            >
              <td className="py-1 font-mono text-[#37352f]">
                {onToggle && !row.locked ? (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#37352f]"
                      checked={row.checked === true}
                      onChange={() => onToggle(row.label)}
                    />
                    <span>{row.label}</span>
                  </label>
                ) : (
                  row.label
                )}
              </td>
              <td className="py-1 text-right tabular-nums text-[#37352f]">
                {row.count.toLocaleString('en-GB')}
              </td>
              <td className="py-1 text-right tabular-nums text-[#73726e]">{formatBytes(row.bytes)}</td>
              {row.extra && (
                <td className="py-1 pl-3 text-right text-[#73726e]">{row.extra}</td>
              )}
              {onToggle && (
                <td className="py-1 pl-3 text-right text-[#73726e]">
                  {row.locked ? 'n/a' : row.checked ? 'copy' : 'skip'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Collapsed>
  );
}
